import {makeAutoObservable, observable} from "mobx";
import {ViewController} from "data/types/structure";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {ILocalizationStore} from "data/stores/localization/localization.store";
import {DateTime} from "luxon";
import type {IContestsStore} from "data/stores/contests/contestsStore";
import {ContestStatus} from "data/enums";
import {IRosterPlayer} from "data/types/roster";
import type {IRoundSummaryStore} from "data/stores/round_summary/round_summary.store";
import {GAME_TIMEZONE} from "data/constants";

interface IParams {
	contestId: number;
}
export interface ILevelContestRosterController extends ViewController<IParams> {
	i18n: ILocalizationStore;

	get isOpen(): boolean;

	get players(): IRosterPlayer[];

	get points(): string | number;

	toggleState: () => void;

	get isPlayed(): boolean;

	get isExpandable(): boolean;

	get contestDay(): string;

	get status(): ContestStatus;
}

@injectable()
export class LevelContestRosterController implements ILevelContestRosterController {
	@observable private _contestId = 0;
	@observable private _isOpen = false;
	constructor(
		@inject(Bindings.LocalizationStore) public readonly i18n: ILocalizationStore,
		@inject(Bindings.ContestsStore) private readonly _contestsStore: IContestsStore,
		@inject(Bindings.RoundSummaryStore) private readonly _rosterSummaryStore: IRoundSummaryStore
	) {
		makeAutoObservable(this);
	}

	init(param: IParams) {
		this._contestId = param.contestId;
	}

	get isOpen(): boolean {
		return this._isOpen;
	}

	toggleState = () => {
		this._isOpen = !this._isOpen;
	};

	get isPlayed() {
		const contest = this.contest;
		if (contest?.status === ContestStatus.Open) {
			return true;
		}
		return !!this.contestSummary;
	}

	get players(): IRosterPlayer[] {
		return this.contestSummary?.roster?.players || [];
	}

	get points(): string | number {
		return this.contestSummary?.ranking?.points || "--";
	}

	get isExpandable() {
		const contest = this.contest;
		return this.isPlayed && contest?.status !== ContestStatus.Open;
	}

	get contestDay(): string {
		const currentContest = this._contestsStore.getContestById(this._contestId);
		if (!currentContest) {
			return "-";
		}

		const contestDate = DateTime.fromISO(currentContest.date).setZone(GAME_TIMEZONE);
		return contestDate.isValid ? contestDate.toFormat("L/dd") : "-";
	}

	get status(): ContestStatus {
		return this.contest?.status ?? ContestStatus.Open;
	}

	private get contest() {
		return this._contestsStore.getContestById(this._contestId);
	}

	private get contestSummary() {
		return this._rosterSummaryStore.getSummaryByContestId(this._contestId);
	}

	onChange(param: IParams) {
		this._contestId = param.contestId;
	}
}
